/**
 * iter150AZ.74 — Calculate-Shipping modal (Slice B).
 *
 * Walks the admin through:
 *   1. Pick origin warehouse per line (uses /shipping/origin-options).
 *      Lines with NULL DbSheetAction get a substitute-part picker.
 *   2. Optionally save the picks (/shipping/set-origin).
 *   3. Validate every address in parallel (/shipping/validate-addresses).
 *      Block on any "fail" and surface the inline red banner.
 *   4. Fetch rates (/shipping/calculate) — backend handles grouping,
 *      FedEx vs LTL split, currency conversion, and persists the
 *      final number to Quote_Heading.Shipping.
 *   5. Review: show winners + (super-admin) raw audit drawer.
 *
 * Super-admin sees an "Audit details" panel with every carrier's
 * full request body + raw response.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Truck, ArrowsClockwise, Warning, CheckCircle, X as XIcon,
  CaretRight, CaretDown, Lightning, Package, Sparkle, ShieldWarning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";

import {
  collectShippingRates, fetchShippingHealth,
  selectShippingRate,
} from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax  = axios.create({ withCredentials: true });

const readDirectOverride = () => {
  if (typeof window === "undefined") return null;
  const value = (new URLSearchParams(window.location.search).get("direct") || "")
    .trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(value)) return true;
  if (["0", "false", "no", "off"].includes(value)) return false;
  return null;
};

const directRequestConfig = (direct) => (
  direct == null ? {} : { params: { direct: direct ? 1 : 0 } }
);


// iter172 — Format FastAPI / axios error detail payloads safely so
// they never leak a raw object into a React text node (the dreaded
// "Objects are not valid as a React child" #31 error).
//
// FastAPI 422 bodies look like:
//   {detail: [{type, loc, msg, input, url}, …]}
// 5xx bodies are usually:
//   {detail: "Failed to load …"}
// and our shipping orchestrator emits:
//   {detail: {error_code, message, …}}
//
// All three are normalised to a single user-facing string here.
function formatErrorDetail(detail, fallback = "Request failed") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((it) => {
      if (typeof it === "string") return it;
      const loc = Array.isArray(it?.loc) ? it.loc.join(".") : "";
      return [loc, it?.msg].filter(Boolean).join(": ");
    }).filter(Boolean).join("; ") || fallback;
  }
  if (typeof detail === "object") {
    if (Array.isArray(detail.items) && detail.items.length) {
      const itemErrors = detail.items.map((item) => {
        const reasons = item.reasons || [item.reason].filter(Boolean);
        return `Line ${item.detail_id}: ${reasons.join(", ")}`;
      }).join("; ");
      return [detail.message, itemErrors].filter(Boolean).join(" ");
    }
    const message = detail.message || detail.msg || detail.error;
    if (message && detail.database_error_code) {
      const category = detail.sql_error_category || detail.sql_error_type;
      const diagnostic = [
        `MariaDB ${detail.database_error_code}`,
        category,
      ].filter(Boolean).join(" / ");
      return `${message} (${diagnostic})`;
    }
    return message || JSON.stringify(detail);
  }
  return String(detail);
}

// Heroic wide-modal styles — uses native dialog for built-in
// focus-trap + Esc handling (cleaner than reaching for shadcn here).
const _overlayCls =
  "fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8";
const _shellCls =
  "bg-white border border-slate-300 rounded-sm shadow-2xl w-full max-w-3xl my-8 overflow-hidden";

function _money(n, currency = "USD") {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency, maximumFractionDigits: 2,
  }).format(Number(n));
}

function _originOptionKey(option) {
  if (!option) return "";
  if (option.selection_key) return String(option.selection_key);
  if (option.inventory_product_id) {
    return `inventory:${option.inventory_product_id}`;
  }
  return option.location_id ? `location:${option.location_id}` : "";
}

function _hasInventoryRecord(option) {
  return Boolean(option?.inventory_product_id)
    && option?.inventory_record_exists !== false;
}

export default function ShippingCalculatorModal({
  quoteId, currency = "USD", isSuperAdmin = false, onClose, onApplied,
}) {
  const [step, setStep] = useState("origin"); // origin | validate | calc | pick | done
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [lines, setLines] = useState([]);          // from /origin-options
  const [picks, setPicks] = useState({});          // {detail_id: {origin_location_id, inventory_product_id, ...}}
  // iter150AZ.75 — quote-level Yes/No flags (Quote_Heading columns).
  const [liftNeeded, setLiftNeeded] = useState("No");
  const [insuranceRequired, setInsuranceRequired] = useState("No");
  const [accessorials, setAccessorials] = useState({
    residential_delivery: false,
    limited_access_required: false,
    inside_delivery_required: false,
    delivery_appointment_required: false,
    carrier_notify_before_arrival: false,
    protect_from_freeze: false,
  });
  const [validationErrors, setValidationErrors] = useState([]); // [{key, address, error}]
  const [calcResult, setCalcResult] = useState(null);
  const [destination, setDestination] = useState({});
  const [destinationCountry, setDestinationCountry] = useState("");
  const [customsSigner, setCustomsSigner] = useState("");
  const [customsFilingReference, setCustomsFilingReference] = useState("");

  // iter172 — direct-vendor rate picker.
  //   directMode: true if SHIPPING_USE_DIRECT_VENDORS is on (or
  //              ?direct=1 was passed in the URL) — switches the
  //              calc step from /calculate to /rates and reveals the
  //              per-shipment carrier picker.
  //   rateGroups: [{shipment_id, ok, mode, rates[], auto_selected,
  //                 manual_selection_required, selected_rate_id}]
  const [directMode, setDirectMode] = useState(false);
  const [directOverride] = useState(readDirectOverride);
  const [rateGroups, setRateGroups] = useState([]);

  // Probe the feature flag once on mount.
  useEffect(() => {
    fetchShippingHealth({ direct: directOverride })
      .then((h) => setDirectMode(!!h?.enabled))
      .catch(() => setDirectMode(false));
  }, [directOverride]);

  // ── Step 1: fetch origin options ───────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        const r = await ax.get(
          `${API}/quotes/${quoteId}/shipping/origin-options`,
          directRequestConfig(directOverride));
        if (cancelled) return;
        const lns = r.data?.lines || [];
        setLines(lns);
        setDestination(r.data?.destination || {});
        setDestinationCountry(
          String(r.data?.destination?.country || "").toUpperCase());
        setCustomsSigner(r.data?.customs_signer || "");
        setCustomsFilingReference(
          r.data?.customs_filing_reference || "");
        // iter150AZ.75 — pre-fill toggles from current column values.
        setLiftNeeded(r.data?.lift_needed === "Yes" ? "Yes" : "No");
        setInsuranceRequired(r.data?.insurance_required === "Yes" ? "Yes" : "No");
        setAccessorials({
          residential_delivery: !!r.data?.residential_delivery,
          limited_access_required: !!r.data?.limited_access_required,
          inside_delivery_required: !!r.data?.inside_delivery_required,
          delivery_appointment_required: !!r.data?.delivery_appointment_required,
          carrier_notify_before_arrival: !!r.data?.carrier_notify_before_arrival,
          protect_from_freeze: !!r.data?.protect_from_freeze,
        });
        const seed = {};
        for (const l of lns) {
          const profile = l.shipping_profile || {};
          seed[l.detail_id] = {
            origin_location_id: l.chosen_location
              ? String(l.chosen_location) : null,
            inventory_product_id: l.chosen_inventory_product_id
              ? String(l.chosen_inventory_product_id) : null,
            dbsheetaction: l.substitute_code || "",
            packaging_type: profile.packaging_type || "parcel",
            package_count: profile.package_count || l.quantity || 1,
            package_weight_lb: profile.package_weight_lb ?? l.weight ?? "",
            package_length_in: profile.package_length_in ?? l.length ?? "",
            package_width_in: profile.package_width_in ?? l.width ?? "",
            package_height_in: profile.package_height_in ?? l.height ?? "",
            freight_class: profile.freight_class || "",
            nmfc_code: profile.nmfc_code || "",
            country_of_origin: profile.country_of_origin || "",
            harmonized_code: profile.harmonized_code || "",
            stackable: !!profile.stackable,
            hazmat: !!profile.hazmat,
            hazmat_class: profile.hazmat_class || "",
            save_profile: false,
          };
        }
        setPicks(seed);
      } catch (e) {
        if (!cancelled) {
          setError(formatErrorDetail(
            e?.response?.data?.detail,
            e.message || "Failed to load origin options"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [quoteId, directOverride]);

  const allPicked = useMemo(
    () => lines.every((l) => picks[l.detail_id]?.origin_location_id
                        && (picks[l.detail_id]?.dbsheetaction
                            || l.ship_code || "").trim()),
    [lines, picks],
  );
  const crossBorderOrigins = useMemo(() => {
    const countries = new Set();
    lines.forEach((line) => {
      const pick = picks[line.detail_id] || {};
      const origin = (line.options || []).find(
        (option) => String(option.location_id || "")
          === String(pick.origin_location_id || "")
          && (!pick.inventory_product_id
            || String(option.inventory_product_id || "")
              === String(pick.inventory_product_id)));
      const originCountry = String(origin?.country || "").toUpperCase();
      if (originCountry && destinationCountry
          && originCountry !== destinationCountry) {
        countries.add(originCountry);
      }
    });
    return Array.from(countries);
  }, [lines, picks, destinationCountry]);
  const crossBorder = crossBorderOrigins.length > 0;
  const customsReady = !crossBorder
    || (!!customsSigner.trim() && !!customsFilingReference.trim());
  const profilesReady = useMemo(() => lines.every((line) => {
    const pick = picks[line.detail_id] || {};
    const values = [
      pick.package_weight_lb,
      pick.package_length_in,
      pick.package_width_in,
      pick.package_height_in,
    ].map(Number);
    if (Number(pick.package_count || 0) < 1
        || values.some((value) => !Number.isFinite(value) || value <= 0)) {
      return false;
    }
    const dimensions = values.slice(1).sort((a, b) => b - a);
    const isLtl = ["pallet", "crate"].includes(pick.packaging_type)
      || !!pick.hazmat
      || values[0] > 150
      || dimensions[0] > 108
      || dimensions[0] + 2 * (dimensions[1] + dimensions[2]) > 165;
    if (isLtl && (!pick.freight_class || !pick.nmfc_code)) return false;
    const selected = (line.options || []).find((option) => (
      String(option.location_id || "") === String(
        pick.origin_location_id || "")
      && (!pick.inventory_product_id
        || String(option.inventory_product_id || "") === String(
          pick.inventory_product_id))
    ));
    const lineCrossBorder = selected?.country && destinationCountry
      && String(selected.country).toUpperCase() !== destinationCountry;
    return !lineCrossBorder
      || (!!pick.country_of_origin && !!pick.harmonized_code);
  }), [lines, picks, destinationCountry]);

  const saveOrigins = useCallback(async () => {
    setSaving(true); setError("");
    try {
      const items = lines.map((l) => ({
        detail_id:     l.detail_id,
        ...picks[l.detail_id],
        dbsheetaction: picks[l.detail_id].dbsheetaction || null,
      }));
      await ax.post(
        `${API}/quotes/${quoteId}/shipping/set-origin`,
        {
          items,
          lift_needed:        liftNeeded,
          insurance_required: insuranceRequired,
          customs_signer: customsSigner.trim() || null,
          customs_filing_reference:
            customsFilingReference.trim() || null,
          ...accessorials,
        },
        directRequestConfig(directOverride),
      );
      toast.success(`Saved origins for ${items.length} line(s)`);
      setStep("validate");
    } catch (e) {
      setError(formatErrorDetail(
        e?.response?.data?.detail,
        e.message || "Could not save origins"));
    } finally { setSaving(false); }
  }, [lines, picks, quoteId, liftNeeded, insuranceRequired,
      accessorials, customsSigner, customsFilingReference, directOverride]);

  // ── Step 2: validate addresses ────────────────────────────────
  const runValidation = useCallback(async () => {
    setSaving(true); setError(""); setValidationErrors([]);
    try {
      const response = await ax.post(
        `${API}/quotes/${quoteId}/shipping/validate-addresses`, {},
        directRequestConfig(directOverride));
      if (response.data?.ok !== true) {
        setValidationErrors(
          response.data?.failures || response.data?.results || []);
        setError("One or more shipping addresses could not be validated.");
        return;
      }
      toast.success("All addresses validated successfully");
      setStep("calc");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && !Array.isArray(detail)
          && detail.error_code === "address_validation_failed") {
        setValidationErrors(detail.failures || []);
        setError("");
      } else {
        setError(formatErrorDetail(detail,
          e.message || "Validation request failed"));
      }
    } finally { setSaving(false); }
  }, [quoteId, directOverride]);

  // ── Step 3: calculate rates ───────────────────────────────────
  // In direct mode we call /rates and surface a per-shipment carrier
  // picker. In legacy mode we still hit /calculate and skip straight
  // to "done" with a single total.
  const runCalculate = useCallback(async () => {
    setSaving(true); setError("");
    try {
      if (directMode) {
        const r = await collectShippingRates(
          quoteId, { direct: directOverride });
        const groups = (r?.shipments || []).map((s) => ({
          ...s,
          selected_rate_id: (s.auto_selected || {}).rate_id || null,
        }));
        setRateGroups(groups);
        setStep("pick");
        // Auto-confirm if every group already has a selection AND
        // none requires manual review (PARCEL with clear winner).
        const allAuto = groups.length > 0
                       && groups.every((g) => g.selected_rate_id
                                              && !g.manual_selection_required);
        if (allAuto) {
          toast.success("All rates auto-selected — review and confirm.");
        }
      } else {
        const r = await ax.post(
          `${API}/quotes/${quoteId}/shipping/calculate`, {},
          directRequestConfig(directOverride));
        setCalcResult(r.data);
        setStep("done");
        toast.success(`Shipping calculated: ${_money(r.data.final_total, currency)}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(formatErrorDetail(detail, e.message || "Calculation failed"));
      // If grouping/origins were incomplete, jump back to step 1.
      if (detail && typeof detail === "object" && !Array.isArray(detail)
          && detail.error_code === "origin_unassigned") setStep("origin");
    } finally { setSaving(false); }
  }, [quoteId, currency, directMode, directOverride]);

  // ── Step 4 (direct mode only): commit the per-shipment selections
  // and persist the total back to Quote_Heading.Shipping.
  const confirmRateSelections = useCallback(async () => {
    setSaving(true); setError("");
    try {
      let total = 0;
      let curr = currency;
      const decisions = [];
      for (const g of rateGroups) {
        if (!g.selected_rate_id) {
          throw new Error(`Shipment ${g.shipment_id} has no rate selected.`);
        }
        // Persist the selection (server logs the decision in the audit).
        if (!g.already_booked) {
          await selectShippingRate(quoteId, g.selected_rate_id,
                                     "confirmed in modal v2",
                                     { direct: directOverride });
        }
        const chosen = (g.rates || []).find((r) => r.rate_id === g.selected_rate_id);
        if (chosen) {
          total += Number(chosen.price || 0);
          curr = chosen.currency || curr;
          decisions.push({
            shipment_id: g.shipment_id,
            mode:        g.mode,
            rate_id:     chosen.rate_id,
            carrier:     chosen.carrier_name,
            service:     chosen.service_level,
            price:       chosen.price,
            currency:    chosen.currency,
          });
        }
      }
      const result = {
        ok:            true,
        final_total:   Math.round(total * 100) / 100,
        currency:      curr,
        shipments:     decisions,
        _direct_vendor: true,
      };
      setCalcResult(result);
      setStep("done");
      toast.success(`Shipping confirmed: ${_money(total, curr)}`);
    } catch (e) {
      setError(formatErrorDetail(
        e?.response?.data?.detail,
        e.message || "Rate confirmation failed"));
    } finally { setSaving(false); }
  }, [rateGroups, quoteId, currency, directOverride]);

  return (
    <div className={_overlayCls} data-testid="shipping-modal" role="dialog" aria-modal="true">
      <div className={_shellCls}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-slate-900 text-white">
          <div className="flex items-center gap-2">
            <Truck size={18} weight="duotone" />
            <span className="font-semibold text-sm">Calculate Shipping — Quote #{quoteId}</span>
          </div>
          <button onClick={onClose}
                  data-testid="shipping-modal-close"
                  className="text-slate-300 hover:text-white transition-colors">
            <XIcon size={18} weight="bold" />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-5 py-2 bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 flex items-center gap-3 flex-wrap">
          {(directMode
              ? ["origin", "validate", "calc", "pick", "done"]
              : ["origin", "validate", "calc", "done"]
           ).map((s, i, arr) => (
            <span key={s} data-testid={`shipping-step-${s}`}
                  className={"flex items-center gap-1.5 " +
                    (step === s ? "text-blue-700 font-bold" : "")}>
              <span className={"inline-block w-4 h-4 rounded-full text-[10px] flex items-center justify-center " +
                (step === s ? "bg-blue-700 text-white" : "bg-slate-200 text-slate-600")}>{i + 1}</span>
              {s === "origin"   && "Origins"}
              {s === "validate" && "Validate"}
              {s === "calc"     && "Rates"}
              {s === "pick"     && "Pick carrier"}
              {s === "done"     && "Review"}
              {i < arr.length - 1 && <CaretRight size={10} weight="bold" className="opacity-50" />}
            </span>
          ))}
          {directMode && (
            <span data-testid="shipping-direct-mode-badge"
                  className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-sm px-1.5 py-0.5">
              <Sparkle size={10} weight="fill" /> Direct vendors
            </span>
          )}
        </div>

        {/* Body */}
        <div className="px-5 py-4 max-h-[70vh] overflow-y-auto">
          {loading && (
            <div className="py-12 text-center text-sm text-slate-500"
                 data-testid="shipping-loading">Loading…</div>
          )}

          {!loading && error && (
            <div className="mb-3 px-3 py-2 rounded-sm bg-rose-50 border border-rose-200 text-rose-800 text-sm inline-flex items-start gap-2"
                 data-testid="shipping-error">
              <Warning size={16} weight="duotone" className="mt-0.5 shrink-0" />
              <span>{typeof error === "string" ? error : formatErrorDetail(error)}</span>
            </div>
          )}

          {!loading && step === "origin" && (
            <OriginStep
              lines={lines}
              picks={picks}
              setPicks={setPicks}
              liftNeeded={liftNeeded}
              setLiftNeeded={setLiftNeeded}
              insuranceRequired={insuranceRequired}
              setInsuranceRequired={setInsuranceRequired}
              accessorials={accessorials}
              setAccessorials={setAccessorials}
              crossBorder={crossBorder}
              crossBorderOrigins={crossBorderOrigins}
              customsSigner={customsSigner}
              setCustomsSigner={setCustomsSigner}
              customsFilingReference={customsFilingReference}
              setCustomsFilingReference={setCustomsFilingReference}
              destination={destination}
              currency={currency} />
          )}

          {!loading && step === "validate" && (
            <ValidateStep
              errors={validationErrors}
              onRetry={() => setStep("origin")} />
          )}

          {!loading && step === "calc" && (
            <div className="py-6 text-center text-sm text-slate-600" data-testid="shipping-calc-prompt">
              <p>Addresses validated. Ready to fetch rates from FedEx + LTL carriers.</p>
              <p className="text-xs text-slate-500 mt-1">
                {directMode
                  ? "Direct mode: EasyPost / C.H. Robinson / Coyote-RXO will be queried in parallel; you'll confirm the carrier next."
                  : "The saved physical packing plan determines parcel versus LTL and the carrier request."}
              </p>
            </div>
          )}

          {!loading && step === "pick" && (
            <PickRateStep
              groups={rateGroups}
              setGroups={setRateGroups}
              currency={currency} />
          )}

          {!loading && step === "done" && calcResult && (
            <DoneStep result={calcResult} currency={currency}
                      isSuperAdmin={isSuperAdmin}
                      quoteId={quoteId} />
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
          {step === "origin" && (
            <>
              <button onClick={onClose} data-testid="shipping-cancel-origin"
                      className="text-xs font-medium px-3 py-1.5 rounded-sm border border-slate-300 hover:bg-white">
                Cancel
              </button>
              <button onClick={saveOrigins}
                      disabled={!allPicked || !profilesReady || !customsReady || saving}
                      data-testid="shipping-save-origins"
                      className="text-xs font-semibold px-4 py-1.5 rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed">
                {saving ? "Saving…" : "Save & Continue"}
              </button>
            </>
          )}
          {step === "validate" && validationErrors.length === 0 && (
            <>
              <button onClick={() => setStep("origin")}
                      className="text-xs font-medium px-3 py-1.5 rounded-sm border border-slate-300 hover:bg-white">
                Back
              </button>
              <button onClick={runValidation} disabled={saving}
                      data-testid="shipping-run-validate"
                      className="text-xs font-semibold px-4 py-1.5 rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 inline-flex items-center gap-1.5">
                <ArrowsClockwise size={12} weight="bold" className={saving ? "animate-spin" : ""} />
                {saving ? "Validating…" : "Run validation"}
              </button>
            </>
          )}
          {step === "calc" && (
            <>
              <button onClick={() => setStep("validate")}
                      className="text-xs font-medium px-3 py-1.5 rounded-sm border border-slate-300 hover:bg-white">
                Back
              </button>
              <button onClick={runCalculate} disabled={saving}
                      data-testid="shipping-run-calc"
                      className="text-xs font-semibold px-4 py-1.5 rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 inline-flex items-center gap-1.5">
                <Lightning size={12} weight="fill" />
                {saving ? "Calculating…" : directMode ? "Get rates" : "Calculate rates"}
              </button>
            </>
          )}
          {step === "pick" && (
            <>
              <button onClick={() => setStep("calc")}
                      data-testid="shipping-pick-back"
                      className="text-xs font-medium px-3 py-1.5 rounded-sm border border-slate-300 hover:bg-white">
                Back
              </button>
              <button
                onClick={confirmRateSelections}
                disabled={saving
                          || rateGroups.length === 0
                          || rateGroups.some((g) => !g.selected_rate_id)}
                data-testid="shipping-confirm-pick"
                className="text-xs font-semibold px-4 py-1.5 rounded-sm bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
                <CheckCircle size={12} weight="fill" />
                {saving ? "Confirming…" : "Confirm rates"}
              </button>
            </>
          )}
          {step === "done" && (
            <button onClick={() => { onApplied?.(calcResult); onClose?.(); }}
                    data-testid="shipping-modal-apply"
                    className="text-xs font-semibold px-4 py-1.5 rounded-sm bg-emerald-700 text-white hover:bg-emerald-800 inline-flex items-center gap-1.5">
              <CheckCircle size={12} weight="fill" /> Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function OriginStep({ lines, picks, setPicks,
                     liftNeeded, setLiftNeeded,
                     insuranceRequired, setInsuranceRequired,
                     accessorials, setAccessorials,
                     crossBorder, crossBorderOrigins,
                     customsSigner, setCustomsSigner,
                     customsFilingReference, setCustomsFilingReference,
                     destination }) {
  const [advancedOpen, setAdvancedOpen] = useState(() => lines.some((line) => {
    const profile = line.shipping_profile || {};
    return line.origin_resolution_required || profile.review_required
      || (crossBorder
        && (!profile.country_of_origin || !profile.harmonized_code));
  }));
  const needsAdvanced = lines.some((line) => {
    const profile = line.shipping_profile || {};
    return line.origin_resolution_required || profile.review_required
      || (crossBorder
        && (!profile.country_of_origin || !profile.harmonized_code));
  });
  useEffect(() => {
    if (needsAdvanced) setAdvancedOpen(true);
  }, [needsAdvanced]);
  if (!lines.length) {
    return (
      <div className="py-8 text-center text-sm text-slate-500">
        This quote has no line items. Add at least one before calculating shipping.
      </div>
    );
  }
  const accessorialOptions = [
    ["residential_delivery", "Residential"],
    ["limited_access_required", "Limited access"],
    ["inside_delivery_required", "Inside delivery"],
    ["delivery_appointment_required", "Delivery appointment"],
    ["carrier_notify_before_arrival", "Notify before arrival"],
    ["protect_from_freeze", "Protect from freeze"],
  ];
  const setPickField = (detailId, field, value) => {
    setPicks((current) => ({
      ...current,
      [detailId]: { ...(current[detailId] || {}), [field]: value },
    }));
  };
  const setOrigin = (detailId, option) => {
    setPicks((current) => ({
      ...current,
      [detailId]: {
        ...(current[detailId] || {}),
        origin_location_id: option?.location_id
          ? String(option.location_id) : null,
        inventory_product_id: option?.inventory_product_id
          ? String(option.inventory_product_id) : null,
      },
    }));
  };
  const setProfileField = (detailId, field, value) => {
    setPicks((current) => ({
      ...current,
      [detailId]: {
        ...(current[detailId] || {}),
        [field]: value,
        save_profile: true,
      },
    }));
  };
  const destinationText = destination?.full || [
    destination?.street1 || destination?.street,
    destination?.city,
    destination?.state,
    destination?.zip,
    destination?.country,
  ].filter(Boolean).join(", ");
  return (
    <div className="space-y-3" data-testid="shipping-origin-step">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 pb-3 border-b border-slate-200">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">
            Ship to
          </div>
          <div className="text-sm text-slate-800 break-words" data-testid="shipping-destination">
            {destinationText || "Address required"}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">
            Services
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
          <label className="text-xs inline-flex items-center gap-2">
            <input type="checkbox"
                   checked={liftNeeded === "Yes"}
                   onChange={(e) => setLiftNeeded(e.target.checked ? "Yes" : "No")}
                   data-testid="shipping-lift-needed" />
            Liftgate
          </label>
          <label className="text-xs inline-flex items-center gap-2">
            <input type="checkbox"
                   checked={insuranceRequired === "Yes"}
                   onChange={(e) => setInsuranceRequired(e.target.checked ? "Yes" : "No")}
                   data-testid="shipping-insurance-required" />
            Insurance
          </label>
          </div>
        </div>
      </div>
      {crossBorder && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border border-amber-300 bg-amber-50 p-3">
          <label className="text-xs">
            <span className="text-slate-600">Customs signer</span>
            <input
              type="text"
              value={customsSigner}
              onChange={(event) => setCustomsSigner(event.target.value)}
              data-testid="shipping-customs-signer"
              className="mt-0.5 w-full text-xs border border-slate-300 rounded-sm px-2 py-1 bg-white"
            />
          </label>
          <label className="text-xs">
            <span className="text-slate-600">
              {crossBorderOrigins.includes("US")
                ? "AES citation / NOEEI exemption"
                : crossBorderOrigins.includes("CA")
                  ? "CERS proof / NDR code"
                  : "Export filing reference"}
            </span>
            <input
              type="text"
              value={customsFilingReference}
              onChange={(event) =>
                setCustomsFilingReference(event.target.value)}
              data-testid="shipping-customs-filing-reference"
              className="mt-0.5 w-full text-xs border border-slate-300 rounded-sm px-2 py-1 bg-white"
            />
          </label>
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <div className="text-[10px] font-bold uppercase text-slate-500">
          Fulfillment plan
        </div>
        <button
          type="button"
          onClick={() => setAdvancedOpen((open) => !open)}
          aria-expanded={advancedOpen}
          data-testid="shipping-advanced-toggle"
          className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900"
        >
          {advancedOpen ? <CaretDown size={13} /> : <CaretRight size={13} />}
          Advanced
        </button>
      </div>
      {lines.map((l) => {
        const pick = picks[l.detail_id] || {};
        const opts = l.options || [];
        const selectedOrigin = opts.find((option) => (
          String(option.location_id || "") === String(
            pick.origin_location_id || "")
          && (!pick.inventory_product_id
            || String(option.inventory_product_id || "") === String(
              pick.inventory_product_id))
        ));
        const selectedOriginKey = _originOptionKey(selectedOrigin);
        const originMissing = !selectedOrigin;
        const dimensions = [
          Number(pick.package_length_in || 0),
          Number(pick.package_width_in || 0),
          Number(pick.package_height_in || 0),
        ].sort((a, b) => b - a);
        const isLtl = ["pallet", "crate"].includes(pick.packaging_type)
          || !!pick.hazmat
          || Number(pick.package_weight_lb || 0) > 150
          || dimensions[0] > 108
          || dimensions[0] + 2 * (dimensions[1] + dimensions[2]) > 165;
        const profile = l.shipping_profile || {};
        const originText = selectedOrigin
          ? [selectedOrigin.name,
             [selectedOrigin.city, selectedOrigin.state].filter(Boolean).join(", ")]
              .filter(Boolean).join(" · ")
          : "Warehouse required";
        const stockText = _hasInventoryRecord(selectedOrigin)
          ? `${Number(selectedOrigin.stock || 0).toLocaleString()} in stock`
          : "";
        const packageText = [
          `${pick.package_count || 0} × ${pick.packaging_type || "parcel"}`,
          pick.package_weight_lb ? `${pick.package_weight_lb} lb each` : "",
          dimensions.every((value) => value > 0)
            ? `${pick.package_length_in} × ${pick.package_width_in} × ${pick.package_height_in} in`
            : "",
        ].filter(Boolean).join(" · ");
        const selectedOriginCountry = String(
          selectedOrigin?.country || "").toUpperCase();
        const destinationCountry = String(
          destination?.country || "").toUpperCase();
        const lineCrossBorder = selectedOriginCountry && destinationCountry
          && selectedOriginCountry !== destinationCountry;
        const customsProfileMissing = lineCrossBorder
          && (!pick.country_of_origin || !pick.harmonized_code);
        return (
          <div key={l.detail_id}
               data-testid={`shipping-line-${l.detail_id}`}
               className="border border-slate-200 rounded-sm p-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-mono text-xs text-slate-500">Line #{l.detail_id}</div>
                <div className="text-sm font-semibold">{l.quoted_code || "—"}</div>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className={`font-semibold ${isLtl ? "text-amber-700" : "text-blue-700"}`}>
                  {isLtl ? "LTL" : "Parcel"}
                </span>
                <span>Qty {l.quantity}</span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-slate-500">Ship from</div>
                <div className="font-medium text-slate-800 mt-0.5">{originText}</div>
                {stockText && <div className="text-slate-500 mt-0.5">{stockText}</div>}
              </div>
              <div>
                <div className="text-slate-500">Package</div>
                <div className="font-medium text-slate-800 mt-0.5">{packageText}</div>
                <div className="text-slate-500 mt-0.5">
                  {profile.source === "shipping_product_profiles"
                    ? "Product profile" : "Catalog calculation"}
                </div>
              </div>
            </div>
            {(originMissing || profile.review_required
              || customsProfileMissing) && (
              <div className="mt-3 border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-900">
                {originMissing
                  ? "Select the active warehouse that will ship this line."
                  : profile.review_required
                    ? "Physical package data is incomplete."
                    : "Country of origin and HS tariff code are required."}
              </div>
            )}
            {selectedOrigin && !_hasInventoryRecord(selectedOrigin) && (
              <div className="mt-3 border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-900">
                No inventory record exists for this product at {selectedOrigin.name || "the selected warehouse"}. You can continue calculating shipping; fulfillment must be resolved before carrier booking.
              </div>
            )}
            {l.backorder_required && (
              <div className="mt-3 border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-900">
                Backorder required: the selected warehouse is short {l.backorder_quantity} unit(s).
              </div>
            )}
            {l.stored_origin_invalid && selectedOrigin && (
              <div className="mt-2 text-[11px] text-amber-700">
                The previous warehouse reference was invalid; stock policy selected {selectedOrigin.name}.
              </div>
            )}
            {advancedOpen && (
              <div className="mt-3 pt-3 border-t border-slate-200 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="text-xs">
                    <div className="text-slate-500">Ship part</div>
                    <div className="mt-0.5 min-h-7 flex items-center font-mono text-slate-800 border border-slate-200 bg-slate-50 rounded-sm px-2 py-1"
                         data-testid={`shipping-ship-code-${l.detail_id}`}>
                      {pick.dbsheetaction || l.ship_code || l.quoted_code || "—"}
                    </div>
                  </div>
                  <label className="text-xs">
                    <span className="text-slate-500">Ship from</span>
                <select
                  value={selectedOriginKey}
                  onChange={(e) => setOrigin(
                    l.detail_id,
                    opts.find((option) => (
                      _originOptionKey(option) === e.target.value))
                      || null)}
                  data-testid={`shipping-origin-${l.detail_id}`}
                  className="mt-0.5 w-full text-xs border border-slate-300 rounded-sm px-2 py-1 bg-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">— pick a warehouse —</option>
                  {opts.map((o) => (
                    <option key={_originOptionKey(o)} value={_originOptionKey(o)}>
                      {o.name || `Loc ${o.location_id}`} — {_hasInventoryRecord(o)
                        ? (o.stock > 0 ? `${o.stock} in stock` : "no stock")
                        : "no inventory record"}
                    </option>
                  ))}
                </select>
                {originMissing && (
                  <span className="mt-1 block text-[11px] text-amber-700">
                    Select the warehouse that will fulfill this line.
                  </span>
                )}
              </label>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mt-3 pt-3 border-t border-slate-100">
              <label className="text-xs">
                <span className="text-slate-500">Packaging</span>
                <select
                  value={pick.packaging_type || "parcel"}
                  onChange={(e) => setProfileField(
                    l.detail_id, "packaging_type", e.target.value)}
                  data-testid={`shipping-packaging-${l.detail_id}`}
                  className="mt-0.5 w-full text-xs border border-slate-300 rounded-sm px-2 py-1 bg-white"
                >
                  <option value="parcel">Parcel</option>
                  <option value="pallet">Pallet</option>
                  <option value="crate">Crate</option>
                </select>
              </label>
              {[
                ["package_count", "Packages", "1"],
                ["package_weight_lb", "Each weight (lb)", "0.01"],
                ["package_length_in", "Length (in)", "0.01"],
                ["package_width_in", "Width (in)", "0.01"],
                ["package_height_in", "Height (in)", "0.01"],
              ].map(([key, label, stepValue]) => (
                <label key={key} className="text-xs">
                  <span className="text-slate-500">{label}</span>
                  <input
                    type="number"
                    min={key === "package_count" ? 1 : 0.01}
                    step={stepValue}
                    value={pick[key] ?? ""}
                    onChange={(e) => setProfileField(
                      l.detail_id, key, e.target.value)}
                    data-testid={`shipping-${key.replaceAll("_", "-")}-${l.detail_id}`}
                    className="mt-0.5 w-full text-xs border border-slate-300 rounded-sm px-2 py-1"
                  />
                </label>
              ))}
            </div>
            {isLtl && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                <label className="text-xs">
                  <span className="text-slate-500">Freight class</span>
                  <input
                    type="text"
                    value={pick.freight_class || ""}
                    onChange={(e) => setProfileField(
                      l.detail_id, "freight_class", e.target.value)}
                    data-testid={`shipping-freight-class-${l.detail_id}`}
                    className="mt-0.5 w-full text-xs border border-slate-300 rounded-sm px-2 py-1"
                  />
                </label>
                <label className="text-xs">
                  <span className="text-slate-500">NMFC code</span>
                  <input
                    type="text"
                    value={pick.nmfc_code || ""}
                    onChange={(e) => setProfileField(
                      l.detail_id, "nmfc_code", e.target.value)}
                    data-testid={`shipping-nmfc-${l.detail_id}`}
                    className="mt-0.5 w-full text-xs border border-slate-300 rounded-sm px-2 py-1"
                  />
                </label>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              <label className="text-xs">
                <span className="text-slate-500">Country of origin</span>
                <input
                  type="text"
                  maxLength={2}
                  value={pick.country_of_origin || ""}
                  onChange={(e) => setProfileField(
                    l.detail_id, "country_of_origin",
                    e.target.value.toUpperCase())}
                  data-testid={`shipping-country-origin-${l.detail_id}`}
                  className="mt-0.5 w-full text-xs uppercase border border-slate-300 rounded-sm px-2 py-1"
                />
              </label>
              <label className="text-xs">
                <span className="text-slate-500">HS tariff code</span>
                <input
                  type="text"
                  value={pick.harmonized_code || ""}
                  onChange={(e) => setProfileField(
                    l.detail_id, "harmonized_code", e.target.value)}
                  data-testid={`shipping-hs-code-${l.detail_id}`}
                  className="mt-0.5 w-full text-xs border border-slate-300 rounded-sm px-2 py-1"
                />
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3">
              <label className="text-xs inline-flex items-center gap-2">
                <input type="checkbox"
                       checked={!!pick.stackable}
                       onChange={(e) => setProfileField(
                         l.detail_id, "stackable", e.target.checked)} />
                Stackable
              </label>
              <label className="text-xs inline-flex items-center gap-2">
                <input type="checkbox"
                       checked={!!pick.hazmat}
                       onChange={(e) => setProfileField(
                         l.detail_id, "hazmat", e.target.checked)} />
                Hazardous
              </label>
              {pick.hazmat && (
                <label className="text-xs inline-flex items-center gap-2">
                  <span className="text-slate-500">Hazmat class</span>
                  <input type="text"
                         value={pick.hazmat_class || ""}
                         onChange={(e) => setProfileField(
                           l.detail_id, "hazmat_class", e.target.value)}
                         className="w-28 text-xs border border-slate-300 rounded-sm px-2 py-1" />
                </label>
              )}
              <label className="text-xs inline-flex items-center gap-2 ml-auto">
                <input type="checkbox"
                       checked={pick.save_profile !== false}
                       onChange={(e) => setPickField(
                         l.detail_id, "save_profile", e.target.checked)} />
                Save as product default
              </label>
            </div>
              </div>
            )}
          </div>
        );
      })}
      {advancedOpen && (
        <div className="border-t border-slate-200 pt-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-2">
            {accessorialOptions.map(([key, label]) => (
              <label key={key} className="text-xs inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!accessorials[key]}
                  onChange={(e) => setAccessorials({
                    ...accessorials, [key]: e.target.checked,
                  })}
                  data-testid={`shipping-${key.replaceAll("_", "-")}`}
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function ValidateStep({ errors, onRetry }) {
  if (!errors.length) {
    return (
      <div className="py-6 text-sm text-slate-600">
        <p>Click <strong>Run validation</strong> to verify every address (ship-to + each origin warehouse) with the carrier.</p>
        <p className="text-xs text-slate-500 mt-2">If any address fails, you&apos;ll see exactly which one + the upstream reason here, and an email is sent to the quote&apos;s preparer.</p>
      </div>
    );
  }
  return (
    <div data-testid="shipping-validate-errors">
      <div className="bg-rose-50 border border-rose-200 text-rose-900 rounded-sm p-3 mb-3 text-sm">
        <div className="font-semibold mb-1">Address validation failed</div>
        <p>Please update the address(es) below on the customer record or the warehouse settings, then click Back to retry.</p>
      </div>
      <ul className="space-y-2">
        {errors.flatMap((failure) => {
          if (failure.key || failure.address || failure.error) return [failure];
          const nested = [];
          if (failure.dest && !failure.dest.ok) {
            nested.push({
              key: "Ship to",
              address: failure.dest.address,
              error: failure.dest.error?.message || failure.dest.reason,
            });
          }
          if (failure.from && !failure.from.ok) {
            nested.push({
              key: "Ship from",
              address: failure.from.address,
              error: failure.from.error?.message || failure.from.reason,
            });
          }
          if (!nested.length) {
            nested.push({
              key: failure.shipment_id ? `Shipment ${failure.shipment_id}` : "Address",
              address: {},
              error: failure.reason || "Address could not be validated.",
            });
          }
          return nested;
        }).map((e, i) => (
          <li key={i} data-testid={`shipping-validate-error-${i}`}
              className="border border-slate-200 rounded-sm p-2 text-xs">
            <div className="font-mono text-slate-500">{e.key || "Address"}</div>
            <div className="text-slate-800">
              {(e.address?.full) ||
               [e.address?.street1 || e.address?.street, e.address?.city,
                e.address?.state, e.address?.zip, e.address?.country]
                .filter(Boolean).join(", ") || "—"}
            </div>
            <div className="text-rose-700 mt-0.5">
              {typeof e.error === "string"
                ? e.error
                : e.error?.message || "Address could not be validated."}
            </div>
          </li>
        ))}
      </ul>
      <button onClick={onRetry}
              className="mt-3 text-xs font-medium px-3 py-1.5 rounded-sm border border-slate-300 hover:bg-white">
        Back to origins
      </button>
    </div>
  );
}


function DoneStep({
  result, currency, isSuperAdmin, quoteId,
}) {
  const [showAudit, setShowAudit] = useState(false);
  const rows = result.shipments || result.winners || [];
  return (
    <div data-testid="shipping-done-step">
      <div className="text-center py-3">
        <div className="text-xs uppercase tracking-wider text-slate-500">Total shipping</div>
        <div className="text-3xl font-black font-mono text-slate-900 mt-1"
             data-testid="shipping-final-total">
          {_money(result.final_total, currency)}
        </div>
        <div className="text-[11px] text-slate-500 mt-1">
          Written to Quote_Heading.Shipping for Quote #{quoteId}
        </div>
      </div>

      {/* Winners table */}
      <div className="mt-4 border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-xs" data-testid="shipping-winners-table">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="text-left px-2 py-1.5">Group</th>
              <th className="text-left px-2 py-1.5">Mode</th>
              <th className="text-left px-2 py-1.5">Carrier</th>
              <th className="text-right px-2 py-1.5">
                Native price ({result.conversion?.source_currency || "CAD"})
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((w, i) => (
              <tr key={i} className="border-t border-slate-100"
                  data-testid={`shipping-winner-${i}`}>
                <td className="px-2 py-1.5 font-mono text-slate-700">
                  {w.origin_key || w.shipment_id?.slice(-8) || "—"}
                </td>
                <td className="px-2 py-1.5">{w.mode || "—"}</td>
                <td className="px-2 py-1.5">
                  {w.carrier_hint || w.carrier || "—"}
                  {w.service ? ` · ${w.service}` : ""}
                </td>
                <td className="px-2 py-1.5 text-right font-mono">
                  {_money(w.price_native ?? w.price, w.currency || currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Conversion notice */}
      {result.conversion?.source_currency !== result.conversion?.target_currency && (
        <div className="mt-3 text-[11px] text-slate-500">
          Converted from {result.conversion.source_currency} {result.conversion.native_total.toFixed(2)}
          {" "}× (1 / {result.conversion.divisor}) →
          {" "}{result.conversion.target_currency} {result.conversion.converted_total.toFixed(2)}
        </div>
      )}

      {/* Failed groups (if any) */}
      {(result.failed_groups || []).length > 0 && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-sm p-2 text-xs">
          <div className="font-semibold mb-1 text-amber-800">
            {result.failed_groups.length} group(s) returned no usable rate
          </div>
          <ul className="text-amber-900">
            {result.failed_groups.map((f, i) => (
              <li key={i} className="font-mono">
                {f.origin_key} ({f.mode}): {(f.errors || []).join(" | ") || "no rate"}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Super-admin: audit drawer */}
      {isSuperAdmin && (
        <div className="mt-4 border-t border-slate-200 pt-3"
             data-testid="shipping-audit-drawer">
          <button
            onClick={() => setShowAudit(!showAudit)}
            data-testid="shipping-audit-toggle"
            className="text-xs font-semibold text-slate-700 hover:text-blue-700 inline-flex items-center gap-1"
          >
            {showAudit ? <CaretDown size={12} /> : <CaretRight size={12} />}
            Audit details (super-admin)
          </button>
          {showAudit && (
            <div className="mt-2 bg-slate-50 border border-slate-200 rounded-sm p-2 max-h-64 overflow-y-auto"
                 data-testid="shipping-audit-content">
              <pre className="text-[10px] font-mono whitespace-pre-wrap">
{JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// iter172 — Per-shipment carrier picker.
//
// PARCEL groups: the backend already auto-selects the cheapest rate
// (price gap > 5 %). The user can override by tapping a row.
// LTL groups: backend NEVER auto-selects — the user MUST pick a row
// before "Confirm rates" enables. The amber banner above the
// rate table makes that requirement obvious.
function PickRateStep({ groups, setGroups, currency }) {
  if (!groups || groups.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-slate-500"
            data-testid="shipping-pick-empty">
        No rates returned. Step back and re-run &quot;Get rates&quot;, or check
        carrier credentials in <span className="font-mono">/admin/settings → Shipping</span>.
      </div>
    );
  }

  const setSelectedRate = (shipmentId, rateId) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.shipment_id === shipmentId
          ? { ...g, selected_rate_id: rateId }
          : g
      )
    );
  };

  return (
    <div className="space-y-4" data-testid="shipping-pick-step">
      {groups.map((g) => {
        const rates = g.rates || [];
        const mode = (g.mode || "").toUpperCase();
        const isLTL = mode === "LTL";
        const hasSelection = !!g.selected_rate_id;
        const isAutoPicked = (g.auto_selected || {}).rate_id === g.selected_rate_id;
        return (
          <div key={g.shipment_id}
                data-testid={`shipping-pick-group-${g.shipment_id}`}
                className="border border-slate-200 rounded-sm bg-white overflow-hidden">
            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center gap-2 min-w-0">
                <Package size={14} weight="bold" className="text-slate-500" />
                <span className="text-xs font-bold text-slate-800 truncate">
                  Shipment {g.shipment_id.slice(-6)}
                </span>
                <span className={"text-[10px] uppercase tracking-wider font-bold rounded-sm px-1.5 py-0.5 border "
                                + (isLTL
                                    ? "bg-amber-50 text-amber-800 border-amber-200"
                                    : "bg-indigo-50 text-indigo-800 border-indigo-200")}>
                  {mode || "?"}
                </span>
                {isAutoPicked && (
                  <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5"
                          data-testid={`shipping-pick-auto-${g.shipment_id}`}>
                    Auto · cheapest
                  </span>
                )}
              </div>
              <span className="text-[11px] text-slate-500 font-mono">
                {rates.length} option{rates.length === 1 ? "" : "s"}
              </span>
            </div>

            {/* LTL banner — surfaces the LTL-must-confirm business rule. */}
            {isLTL && (
              <div data-testid={`shipping-pick-ltl-banner-${g.shipment_id}`}
                    className="flex items-start gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-900">
                <ShieldWarning size={14} weight="duotone" className="shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold">LTL requires confirmation.</span>{" "}
                  Auto-selection is disabled for less-than-truckload freight — please review accessorials, transit days, and carrier reliability before confirming.
                </div>
              </div>
            )}

            {rates.length === 0 ? (
              <div className="px-3 py-4 text-xs text-slate-400 text-center"
                    data-testid={`shipping-pick-no-rates-${g.shipment_id}`}>
                No rates returned for this shipment. {g.reason || ""}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-left text-slate-600">
                    <tr>
                      <th className="px-2 py-1.5 font-semibold w-6"></th>
                      <th className="px-2 py-1.5 font-semibold">Carrier</th>
                      <th className="px-2 py-1.5 font-semibold">Service</th>
                      <th className="px-2 py-1.5 font-semibold text-right">Transit</th>
                      <th className="px-2 py-1.5 font-semibold text-right">Price</th>
                      <th className="px-2 py-1.5 font-semibold text-right">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rates.map((r) => {
                      const checked = g.selected_rate_id === r.rate_id;
                      const filtered = r.selection_status === "filtered";
                      return (
                        <tr key={r.rate_id}
                             data-testid={`shipping-rate-${g.shipment_id}-${r.rate_id}`}
                             onClick={() => !filtered && setSelectedRate(g.shipment_id, r.rate_id)}
                             className={"border-t border-slate-100 cursor-pointer "
                                        + (checked ? "bg-indigo-50 " : "hover:bg-slate-50 ")
                                        + (filtered ? "opacity-50 cursor-not-allowed" : "")}>
                          <td className="px-2 py-1.5 text-center">
                            <input
                              type="radio"
                              name={`pick-${g.shipment_id}`}
                              checked={checked}
                              disabled={filtered}
                              data-testid={`shipping-rate-radio-${g.shipment_id}-${r.rate_id}`}
                              onChange={() => setSelectedRate(g.shipment_id, r.rate_id)}
                              className="cursor-pointer"
                            />
                          </td>
                          <td className="px-2 py-1.5 font-semibold">
                            {r.carrier_name || "—"}
                            {filtered && (
                              <span className="ml-1.5 text-[10px] text-rose-600 font-normal">
                                ({r.selection_reason || "filtered"})
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-slate-600">{r.service_level || "—"}</td>
                          <td className="px-2 py-1.5 text-right font-mono">
                            {r.transit_days != null ? `${r.transit_days} d` : "—"}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono font-bold">
                            {_money(r.price, r.currency || currency)}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-slate-500">
                            {r.score != null ? Number(r.score).toFixed(2) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Selection footer */}
            <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-slate-50 border-t border-slate-200">
              {hasSelection ? (
                <span className="text-[11px] text-emerald-700 font-semibold inline-flex items-center gap-1">
                  <CheckCircle size={11} weight="fill" /> Selection saved
                </span>
              ) : (
                <span className="text-[11px] text-amber-700 font-semibold inline-flex items-center gap-1">
                  <Warning size={11} weight="fill" />
                  {isLTL ? "Pick a carrier" : "No rate selected"}
                </span>
              )}
              <span className="text-[11px] text-slate-400">
                {g.manual_selection_required && "Manual review required"}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
